import _, {without} from 'lodash';
import React, {FC, RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {
  computeHasVisibleScrollbars,
  computeScrollbarWidth,
} from '../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {move} from '../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {FrameTypesEnum} from '../../../../../libs/shared/core/src/models/api/apiTypes';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useSafeArea} from '../../../../../libs/shared/ui/src/components/safeArea/safeAreaContext';
import {SidePanelMultiViewDetailsModel} from '../../../../models/api/localPreferencesModel';
import {SidePanelFrame} from '../../../../store/stateless/sidePanelFrameSelectors';
import {sidePanelSwitcherWidth} from '../sidePanelHelpers';
import {DragLayerSwitcherItem} from './dragLayerSwitcherItem';
import {FrameSwitcherItem, FrameSwitcherItemSharedProps} from './frameSwitcherItem';
import {MultiViewButtonContainer} from './multiView/multiViewButtonContainer';
import {SkeletonSwitcherItem} from './skeletonSwitcherItem';

/*
 * Props.
 */

interface SidePanelSwitcherProps extends FrameSwitcherItemSharedProps {
  isStartGuideIncomplete: boolean;
  visibleFrames: ReadonlyArray<SidePanelFrame>;
  hasP4MultiView?: boolean;
  multiViewDetails: SidePanelMultiViewDetailsModel;
  isApplicationsLoading: boolean;
  onFrameIdsSort: (arg: ReadonlyArray<string>) => void;
  onIsDraggingChange: (arg: boolean) => void;
  panelId: ViewerPanelId;
}

/*
 * Style.
 */

interface SwitcherStyleProps {
  $hasWhiteBackground: boolean;
  $isOpen: boolean;
  $actualScrollbarWidth: number;
  $safeAreaBottom: number;
}

/*
 * Style.
 */

const StyledSkeletonWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 24px;
  padding: 16px 10px;
  width: 64px;
  border-left: solid 2px ${(p) => p.theme.appBackground};
  background-color: ${(p) => p.theme.foreground};
`;

const StyledSwitcherDiv = styled.div<SwitcherStyleProps>`
  overflow: auto;
  overflow-x: hidden;

  background-color: ${(p) => p.theme.foreground};
  border-left: solid 2px ${(p) => p.theme.appBackground};

  padding: 8px 7px ${(p) => 8 + p.$safeAreaBottom}px 7px;
  min-width: ${(p) => p.$actualScrollbarWidth + sidePanelSwitcherWidth}px;
  max-width: ${(p) => p.$actualScrollbarWidth + sidePanelSwitcherWidth}px;
`;

const StyledItemsDiv = styled.div`
  display: grid;
  grid-row-gap: 16px;
  grid-auto-flow: row;
  align-content: start;
`;

const StyledMultiViewWrapperDiv = styled.div`
  &:after {
    display: block;
    content: '';
    margin: 16px auto;
    height: 2px;
    border-radius: 4px;
    background-color: ${(p) => p.theme.greys.shade30};
    width: 24px;
  }
`;

/*
 * Component.
 */

export const SidePanelSwitcher: FC<SidePanelSwitcherProps> = (props) => {
  const {
    visibleFrames,
    hasP4MultiView,
    multiViewDetails,
    isApplicationsLoading,
    onFrameIdsSort,
    onIsDraggingChange,
    panelId,
  } = props;
  const {selectedFrameIds} = multiViewDetails;
  const [dragFrameIds, setDragFrameIds] = useState<ReadonlyArray<string>>();
  const [multiViewDragFrameId, setMultiViewDragFrameId] = useState<string>();
  const [frameIdOverMultiViewButton, setFrameIdOverMultiViewButton] = useState<string>();
  const [isSwitcherOverflowing, setIsSwitcherOverflowing] = useState<boolean>(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const hasVisibleScrollbars = computeHasVisibleScrollbars();
  const scrollbarWidth = computeScrollbarWidth();

  // Add extra blank scrollable area to make room for any floating/foreground UI elements.
  const safeAreaBottom = useSafeAreaBottom(switcherRef);

  // We want to watch whenever a layout event happens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!switcherRef.current) {
      return;
    }
    setIsSwitcherOverflowing(switcherRef.current.scrollHeight > switcherRef.current.offsetHeight);
  });

  const onFrameDragOver = useCallback(
    (frameId: string, overFrameId: string) => {
      // If the selected frame being dragged over is in the multi-view selected frames we are not currently
      // being dragged over the button and should remove the reference as we only send these when over P4 non button.
      if (selectedFrameIds.includes(frameId)) {
        setFrameIdOverMultiViewButton(undefined);
      }

      setDragFrameIds((value) => {
        const array = value || visibleFrames.map((f) => f.frameId);
        const index = array.indexOf(overFrameId);
        return move(array, index, frameId);
      });
    },
    [selectedFrameIds, visibleFrames],
  );

  const onFrameDragStart = useCallback(() => onIsDraggingChange(true), [onIsDraggingChange]);

  const onFrameDragEnd = useCallback(() => {
    if (dragFrameIds) {
      onFrameIdsSort(without(dragFrameIds, FrameTypesEnum.MANAGER));
    }

    setDragFrameIds([]);
    onIsDraggingChange(false);
  }, [dragFrameIds, onFrameIdsSort, setDragFrameIds, onIsDraggingChange]);

  const onMouseMove = useCallback(() => {
    setDragFrameIds((value) => {
      if (!value || value.length > 0) {
        return value;
      }

      return undefined;
    });
  }, [setDragFrameIds]);

  const sortedVisibleFrames = useMemo(
    () =>
      _(visibleFrames)
        .filter(({frameId}) => {
          // If they are not on the feature flag, we can short-circuit this filter.
          if (!hasP4MultiView) {
            return true;
          }

          const isFrameBeingDraggedOverMultiViewButton = frameId === frameIdOverMultiViewButton;
          const isFrameInMultiViewButton = selectedFrameIds.includes(frameId);
          const isFrameFromMultiViewButton = multiViewDragFrameId === frameId;

          // If the frame is not in the multi-view button and filter out the frame if we are currently
          // dragging it over the button.
          if (!isFrameInMultiViewButton) {
            return !isFrameBeingDraggedOverMultiViewButton;
          }

          // If the frame is currently in the multi-view button and it is not being dragged, do not
          // render it in the switcher.
          if (isFrameInMultiViewButton && !isFrameFromMultiViewButton) {
            return false;
          }

          // If the frame is from the multi-view button (already selected) we may need to render it
          // if it is no longer being dragged over the button.
          if (isFrameFromMultiViewButton) {
            return !isFrameBeingDraggedOverMultiViewButton;
          }

          // Return all other items to be rendered.
          return true;
        })
        .sortBy((f) => {
          if (!dragFrameIds) {
            return 0;
          }

          const index = dragFrameIds.indexOf(f.frameId);
          return index < 0 ? Number.MAX_SAFE_INTEGER : index;
        })
        .value(),
    [
      visibleFrames,
      selectedFrameIds,
      multiViewDragFrameId,
      dragFrameIds,
      frameIdOverMultiViewButton,
      hasP4MultiView,
    ],
  );

  const onMultiViewFrameDragEnd = () => {
    setMultiViewDragFrameId(undefined);
    onFrameDragEnd();
  };

  const onMultiViewDragStart = (frameId: string) => {
    setFrameIdOverMultiViewButton(frameId);
    setMultiViewDragFrameId(frameId);
    setDragFrameIds(undefined);
    onFrameDragStart();
  };

  if (isApplicationsLoading) {
    return (
      <StyledSkeletonWrapperDiv>
        <SkeletonSwitcherItem />
        <SkeletonSwitcherItem />
        <SkeletonSwitcherItem />
        <SkeletonSwitcherItem />
        <SkeletonSwitcherItem />
      </StyledSkeletonWrapperDiv>
    );
  }

  const actualScrollbarWidth = hasVisibleScrollbars && isSwitcherOverflowing ? scrollbarWidth : 0;

  return (
    <StyledSwitcherDiv
      ref={switcherRef}
      $hasWhiteBackground={Boolean(props.selectedFrame)}
      $isOpen={Boolean(props.selectedFrame)}
      $actualScrollbarWidth={actualScrollbarWidth}
      $safeAreaBottom={safeAreaBottom}
    >
      {maybeRenderP4MultiViewArea(
        hasP4MultiView,
        frameIdOverMultiViewButton,
        setFrameIdOverMultiViewButton,
        onFrameDragOver,
        onMultiViewDragStart,
        onMultiViewFrameDragEnd,
        panelId,
      )}
      <StyledItemsDiv onMouseMove={onMouseMove}>
        {sortedVisibleFrames.map((frame, index) => (
          <FrameSwitcherItem
            {...props}
            key={frame.frameId}
            frame={frame}
            index={index}
            isOtherDragging={Boolean(dragFrameIds)}
            shouldForceRenderPlaceholder={multiViewDragFrameId === frame.frameId}
            onDragOver={onFrameDragOver}
            onDragStart={onFrameDragStart}
            onDragEnd={onFrameDragEnd}
          />
        ))}
      </StyledItemsDiv>
      <DragLayerSwitcherItem />
    </StyledSwitcherDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderP4MultiViewArea(
  hasP4MultiView: boolean | undefined,
  frameIdOverMultiViewButton: string | undefined,
  setFrameIdOverMultiViewButton: (arg: string | undefined) => void,
  onFrameDragOver: (frameId: string, overFrameId: string) => void,
  onFrameDragStart: (arg: string) => void,
  onFrameDragEnd: () => void,
  panelId: ViewerPanelId,
) {
  if (!hasP4MultiView) {
    return null;
  }
  return (
    <StyledMultiViewWrapperDiv>
      <MultiViewButtonContainer
        frameIdOverMultiViewButton={frameIdOverMultiViewButton}
        setFrameIdOverMultiViewButton={setFrameIdOverMultiViewButton}
        onFrameDragOver={onFrameDragOver}
        onFrameDragStart={onFrameDragStart}
        onFrameDragEnd={onFrameDragEnd}
        panelId={panelId}
      />
    </StyledMultiViewWrapperDiv>
  );
}

function useSafeAreaBottom(switcherRef: RefObject<HTMLElement | null>) {
  const [safeAreaBottom, setSafeAreaBottom] = useState<number>(0);
  const {foregroundSafeArea} = useSafeArea();

  useLayoutEffect(() => {
    const switcher = switcherRef.current;
    const window = switcher?.ownerDocument?.defaultView;
    if (!window || !switcher) {
      return;
    }

    // Determine the distance from the bottom of the switcher to the bottom of the window.
    const switcherRect = switcher.getBoundingClientRect();
    const switcherBottom = window.innerHeight - switcherRect.bottom;

    // Determine how much of the foreground safe area is overlapping with the switcher.
    const switcherSafeAreaBottom = Math.max(0, foregroundSafeArea.bottom - switcherBottom);

    setSafeAreaBottom(switcherSafeAreaBottom);
  }, [switcherRef, foregroundSafeArea.bottom]);

  return safeAreaBottom;
}
